import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtAsOf } from './format';
import { btn, c, card, tbl } from '../../theme';

// Settings · Pod mapping — which pod a caller belongs to. The city step reads
// these tables on every pull, so an edit reaches new calls within one pull;
// calls already stamped keep their pod until someone re-applies from a date.
// Every rule is checked by the server (services/ccPodMapping.js); this screen
// says what was refused in the server's words.

const RULES = { pod: 'Its own pod', follows: 'Follows another code', caller_zip: "Caller's zip decides" };
const errText = (err) => err.response?.data?.error || err.message;
const fmtN = (n) => Number(n || 0).toLocaleString('en-US');

function useSave(onDone) {
  const [state, setState] = useState(null);
  const run = async (fn, ok) => {
    setState({ busy: true });
    try {
      const r = await fn();
      setState({ ok: typeof ok === 'function' ? ok(r) : ok });
      await onDone?.();
      return r;
    } catch (err) {
      setState({ error: errText(err) });
      return null;
    }
  };
  return [state, run, setState];
}

function Status({ state }) {
  if (state?.error) return <span style={s.error} role="alert">{state.error}</span>;
  if (state?.ok) return <span style={s.ok}>{state.ok}</span>;
  return null;
}

function PodSelect({ pods, value, onChange, label, allowCurrent }) {
  return (
    <select aria-label={label} value={value || ''} onChange={(e) => onChange(e.target.value)} style={s.select}>
      <option value="">Choose a pod</option>
      {pods.filter((p) => p.assignable || p.id === allowCurrent).map((p) => (
        <option key={p.id} value={p.id}>{p.displayName}{p.displayName !== p.id ? ` (${p.id})` : ''}</option>
      ))}
    </select>
  );
}

// ── Pods ────────────────────────────────────────────────────────────────────

function PodRow({ pod, pods, refresh }) {
  const [name, setName] = useState(pod.displayName);
  const [into, setInto] = useState('');
  const [state, run] = useSave(refresh);
  const rename = () => run(() => api.put(`/contact-center/settings/pod-mapping/pods/${pod.id}`, { displayName: name }), 'Renamed.');
  const retire = (retired) => run(() => api.put(`/contact-center/settings/pod-mapping/pods/${pod.id}`, { retired }), retired ? 'Retired.' : 'Restored.');
  const merge = () => {
    const target = pods.find((p) => p.id === into);
    if (!window.confirm(`Move ${pod.displayName}'s ${pod.codes} code(s) and ${pod.zips} zip(s) to ${target?.displayName}? Calls already stamped stay where they are until you re-apply.`)) return null;
    return run(() => api.post(`/contact-center/settings/pod-mapping/pods/${pod.id}/merge`, { into }),
      (r) => `Moved ${r.codes} code(s) and ${r.zips} zip(s).`);
  };
  const canMerge = pod.deptId === 'support' && pod.id !== 'IRG' && (pod.codes || pod.zips);
  return (
    <tr style={{ opacity: pod.retiredAt ? 0.6 : 1 }}>
      <td style={s.td}><code>{pod.id}</code></td>
      <td style={s.td}>
        <input aria-label={`${pod.id} name`} value={name} onChange={(e) => setName(e.target.value)} style={s.text} />
        <button type="button" onClick={rename} disabled={name === pod.displayName || state?.busy} style={btn.secondaryXs}>Rename</button>
      </td>
      <td style={s.td}>{pod.deptId}</td>
      <td style={s.num}>{pod.codes}</td>
      <td style={s.num}>{pod.zips}</td>
      <td style={s.num}>{pod.queues}</td>
      <td style={s.td}>
        {canMerge ? (
          <span style={s.inline}>
            <PodSelect pods={pods.filter((p) => p.id !== pod.id)} value={into} onChange={setInto} label={`Merge ${pod.id} into`} />
            <button type="button" onClick={merge} disabled={!into || state?.busy} style={btn.secondaryXs}>Merge</button>
          </span>
        ) : null}
        {pod.id !== 'IRG' && (pod.retiredAt
          ? <button type="button" onClick={() => retire(false)} style={btn.secondaryXs}>Restore</button>
          : <button type="button" onClick={() => retire(true)} disabled={state?.busy} style={btn.secondaryXs}>Retire</button>)}
        {' '}<Status state={state} />
      </td>
    </tr>
  );
}

function Pods({ pods, refresh }) {
  return (
    <section style={s.card} aria-label="Pods">
      <h3 style={s.h3}>Pods</h3>
      <p style={s.blurb}>
        A pod&rsquo;s id is stamped on every call and never changes; its name is what the pages show, so renaming one
        relabels its history too. Merging moves a pod&rsquo;s codes and zips to another pod for new calls. A pod can be
        retired once nothing points at it, including Genesys queues; its past calls stay under it.
      </p>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Id</th><th style={s.th}>Name</th><th style={s.th}>Department</th>
            <th style={s.thNum}>Codes</th><th style={s.thNum}>Zips</th><th style={s.thNum}>Queues</th><th style={s.th} /></tr></thead>
          <tbody>{pods.map((p) => <PodRow key={`${p.id}:${p.displayName}`} pod={p} pods={pods} refresh={refresh} />)}</tbody>
        </table>
      </div>
    </section>
  );
}

// ── Company codes ───────────────────────────────────────────────────────────

function CodeEditor({ initial, pods, codes, refresh, onDone, isNew }) {
  const [d, setD] = useState(initial);
  const [state, run] = useSave(refresh);
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { rule: d.rule, cityName: d.cityName || '' };
    if (d.rule === 'pod') body.podId = d.podId;
    if (d.rule === 'follows') body.followsCode = d.followsCode;
    if (d.retired !== undefined) body.retired = d.retired;
    const r = await run(() => api.put(`/contact-center/settings/pod-mapping/codes/${encodeURIComponent(d.code.trim())}`, body),
      (x) => (x.changed ? (x.created ? 'Added.' : 'Saved.') : 'Nothing changed.'));
    if (r) onDone?.();
  };
  return (
    <div style={s.editor} role="group" aria-label={isNew ? 'Add a code' : `Edit ${d.code}`}>
      {isNew && <label style={s.field}><span style={s.label}>Code</span>
        <input aria-label="Code" value={d.code} onChange={(e) => set('code')(e.target.value)} style={s.textSm} /></label>}
      <label style={s.field}><span style={s.label}>City name</span>
        <input aria-label="City name" value={d.cityName || ''} onChange={(e) => set('cityName')(e.target.value)} style={s.text} /></label>
      <label style={s.field}><span style={s.label}>Pod comes from</span>
        <select aria-label="Pod comes from" value={d.rule} onChange={(e) => set('rule')(e.target.value)} style={s.select}>
          {Object.entries(RULES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></label>
      {d.rule === 'pod' && <label style={s.field}><span style={s.label}>Pod</span>
        <PodSelect pods={pods} value={d.podId} onChange={set('podId')} label="Pod" allowCurrent={initial.podId} /></label>}
      {d.rule === 'follows' && <label style={s.field}><span style={s.label}>Follows</span>
        <select aria-label="Follows" value={d.followsCode || ''} onChange={(e) => set('followsCode')(e.target.value)} style={s.select}>
          <option value="">Choose a code</option>
          {codes.filter((x) => x.code !== d.code && x.rule !== 'caller_zip' && !x.retiredAt).map((x) => (
            <option key={x.code} value={x.code}>{x.code}{x.cityName ? ` · ${x.cityName}` : ''}</option>))}
        </select></label>}
      {!isNew && <label style={s.check}><input type="checkbox" checked={!!d.retired} onChange={(e) => set('retired')(e.target.checked)} /> Retired</label>}
      <div style={s.inline}>
        <button type="button" onClick={save} disabled={state?.busy || (isNew && !d.code.trim())} style={btn.primarySm}>{isNew ? 'Add code' : 'Save'}</button>
        {onDone && !isNew && <button type="button" onClick={onDone} style={btn.secondaryXs}>Cancel</button>}
        <Status state={state} />
      </div>
      {d.rule === 'caller_zip' && <p style={s.hint}>Calls with this code are placed by the caller&rsquo;s zip, as if the code were not there.</p>}
      {!isNew && d.cityName !== initial.cityName && (
        <p style={s.hint}>The City page and map group calls by this name: a new spelling starts a new row there for new calls.</p>)}
    </div>
  );
}

const podLabel = (pods, id) => pods.find((p) => p.id === id)?.displayName || id;

function Codes({ data, refresh, addCode, setAddCode }) {
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const f = filter.trim().toLowerCase();
  const rows = data.codes.filter((x) => !f || x.code.toLowerCase().includes(f) || (x.cityName || '').toLowerCase().includes(f)
    || (x.effectivePod || '').toLowerCase().includes(f) || podLabel(data.pods, x.effectivePod)?.toLowerCase().includes(f));
  return (
    <section style={s.card} aria-label="Company codes">
      <h3 style={s.h3}>Company codes</h3>
      <p style={s.blurb}>
        The caller&rsquo;s account code decides their pod. A code can have its own pod, follow another code (and move with
        it), or leave the pod to the caller&rsquo;s zip. Changes reach new calls within one pull. Calls in the last {data.recentDays} days are shown.
      </p>
      <div style={s.inline}>
        <input aria-label="Filter codes" placeholder="Filter by code, city or pod" value={filter} onChange={(e) => setFilter(e.target.value)} style={s.text} />
        <button type="button" onClick={() => setAddCode({ code: '', cityName: '', rule: 'pod', podId: '' })} style={btn.secondaryXs}>Add a code</button>
      </div>
      {addCode && <CodeEditor key={addCode.code || 'new'} initial={addCode} isNew pods={data.pods} codes={data.codes} refresh={refresh} onDone={() => setAddCode(null)} />}
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr><th style={s.thS}>Code</th><th style={s.thS}>City name</th><th style={s.thS}>Pod comes from</th>
            <th style={s.thS}>Pod now</th><th style={s.thNumS}>Calls</th><th style={s.thS} /></tr></thead>
          <tbody>
            {rows.map((x) => (editing === x.code ? (
              <tr key={x.code}><td colSpan={6} style={s.td}>
                <CodeEditor initial={{ code: x.code, cityName: x.cityName || '', rule: x.rule, podId: x.podId, followsCode: x.followsCode, retired: !!x.retiredAt }}
                  pods={data.pods} codes={data.codes} refresh={refresh} onDone={() => setEditing(null)} />
              </td></tr>
            ) : (
              <tr key={x.code} style={{ opacity: x.retiredAt ? 0.55 : 1 }}>
                <td style={s.td}><code>{x.code}</code></td>
                <td style={s.td}>{x.cityName || <span style={s.faint}>none</span>}</td>
                <td style={s.td}>{x.retiredAt ? 'Retired' : x.rule === 'follows' ? `Follows ${x.followsCode}` : RULES[x.rule]}</td>
                <td style={s.td}>{x.effectivePod ? podLabel(data.pods, x.effectivePod) : <span style={s.faint}>caller&rsquo;s zip</span>}</td>
                <td style={s.num}>{fmtN(x.calls)}</td>
                <td style={s.td}><button type="button" onClick={() => setEditing(x.code)} style={btn.secondaryXs} aria-label={`Edit ${x.code}`}>Edit</button></td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Zips ────────────────────────────────────────────────────────────────────

function ZipEditor({ initial, pods, refresh, onDone, isNew }) {
  const [d, setD] = useState(initial);
  const [state, run] = useSave(refresh);
  const save = async () => {
    const r = await run(() => api.put(`/contact-center/settings/pod-mapping/zips/${encodeURIComponent(d.zip.trim())}`, { cityName: d.cityName, podId: d.podId }),
      (x) => (x.changed ? (x.created ? 'Added.' : 'Saved.') : 'Nothing changed.'));
    if (r) onDone?.();
  };
  return (
    <div style={s.editor} role="group" aria-label={isNew ? 'Add a zip' : `Edit ${d.zip}`}>
      {isNew && <label style={s.field}><span style={s.label}>Zip</span>
        <input aria-label="Zip" value={d.zip} onChange={(e) => setD((x) => ({ ...x, zip: e.target.value }))} style={s.textSm} inputMode="numeric" /></label>}
      <label style={s.field}><span style={s.label}>City name</span>
        <input aria-label="Zip city name" value={d.cityName} onChange={(e) => setD((x) => ({ ...x, cityName: e.target.value }))} style={s.text} /></label>
      <label style={s.field}><span style={s.label}>Pod</span>
        <PodSelect pods={pods} value={d.podId} onChange={(v) => setD((x) => ({ ...x, podId: v }))} label="Zip pod" allowCurrent={initial.podId} /></label>
      <div style={s.inline}>
        <button type="button" onClick={save} disabled={state?.busy} style={btn.primarySm}>{isNew ? 'Add zip' : 'Save'}</button>
        {onDone && !isNew && <button type="button" onClick={onDone} style={btn.secondaryXs}>Cancel</button>}
        <Status state={state} />
      </div>
    </div>
  );
}

function Zips({ data, refresh, addZip, setAddZip }) {
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const f = filter.trim().toLowerCase();
  const rows = data.zips.filter((z) => !f || z.zip.includes(f) || z.cityName.toLowerCase().includes(f)
    || (z.podId || '').toLowerCase().includes(f) || podLabel(data.pods, z.podId)?.toLowerCase().includes(f));
  return (
    <section style={s.card} aria-label="Zips">
      <h3 style={s.h3}>Zips</h3>
      <p style={s.blurb}>
        Used when the caller&rsquo;s account does not decide: no code, a code not in the table, or one left to the zip. Zip
        call counts start when calls began keeping the caller&rsquo;s zip (October 2026).
      </p>
      <div style={s.inline}>
        <input aria-label="Filter zips" placeholder="Filter by zip, city or pod" value={filter} onChange={(e) => setFilter(e.target.value)} style={s.text} />
        <button type="button" onClick={() => setAddZip({ zip: '', cityName: '', podId: '' })} style={btn.secondaryXs}>Add a zip</button>
      </div>
      {addZip && <ZipEditor key={addZip.zip || 'new'} initial={addZip} isNew pods={data.pods} refresh={refresh} onDone={() => setAddZip(null)} />}
      <div style={{ ...s.scroll, maxHeight: '420px' }}>
        <table style={s.table}>
          <thead><tr><th style={s.thS}>Zip</th><th style={s.thS}>City</th><th style={s.thS}>Pod</th><th style={s.thNumS}>Calls</th><th style={s.thS} /></tr></thead>
          <tbody>
            {rows.map((z) => (editing === z.zip ? (
              <tr key={z.zip}><td colSpan={5} style={s.td}>
                <ZipEditor initial={{ zip: z.zip, cityName: z.cityName, podId: z.podId }} pods={data.pods} refresh={refresh} onDone={() => setEditing(null)} />
              </td></tr>
            ) : (
              <tr key={z.zip}>
                <td style={s.td}><code>{z.zip}</code></td><td style={s.td}>{z.cityName}</td>
                <td style={s.td}>{podLabel(data.pods, z.podId) || <span style={s.faint}>none</span>}</td>
                <td style={s.num}>{fmtN(z.calls)}</td>
                <td style={s.td}><button type="button" onClick={() => setEditing(z.zip)} style={btn.secondaryXs} aria-label={`Edit ${z.zip}`}>Edit</button></td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Cities ──────────────────────────────────────────────────────────────────
// A city's pod lives on the codes and zips that carry its name; setting it
// here sets all of them at once. A city split across pods asks first, naming
// the pods, so one click never flattens a split made zip by zip.

const cityPods = (pods, city) => (city.status === 'none' ? 'No pod' : city.pods.map((p) => podLabel(pods, p)).join(', '));

function CityRow({ city, pods, refresh }) {
  const [podId, setPodId] = useState('');
  const [open, setOpen] = useState(false);
  const [state, run] = useSave(refresh);
  const split = city.status === 'split';
  const same = city.status === 'single' && city.pods[0] === podId && !city.unassigned;
  const set = () => {
    const target = podLabel(pods, podId);
    const what = `${city.codes.length} code(s) and ${city.zips.length} zip(s)`;
    const ask = split
      ? `${city.name} is split across ${cityPods(pods, city)}. Put all ${what} on ${target}? Codes that follow another code keep following.`
      : `Move ${city.name}'s ${what} to ${target}? New calls follow within one pull; past calls stay where they are until you re-apply.`;
    if (!window.confirm(ask)) return null;
    return run(() => api.post('/contact-center/settings/pod-mapping/cities', { city: city.name, podId, ...(split ? { allowSplit: true } : {}) }),
      (r) => (!r.changed ? 'Already on that pod.'
        : `Moved ${r.codes} code(s) and ${r.zips} zip(s).${r.following.length ? ` Left following: ${r.following.map((f) => `${f.code} follows ${f.follows}`).join(', ')}.` : ''}`));
  };
  return (
    <>
      <tr>
        <td style={s.td}>{city.name}</td>
        <td style={s.td}>
          {split ? <span style={s.warn}>Split: {cityPods(pods, city)}</span> : cityPods(pods, city)}
          {city.unassigned > 0 && city.status !== 'none' && <span style={s.faint}> · {city.unassigned} with no pod</span>}
        </td>
        <td style={s.num}>{city.codes.length}</td>
        <td style={s.num}>{city.zips.length}</td>
        <td style={s.num}>{fmtN(city.calls)}</td>
        <td style={s.td}>
          <span style={s.inline}>
            <PodSelect pods={pods} value={podId} onChange={setPodId} label={`Pod for ${city.name}`} />
            <button type="button" onClick={set} disabled={!podId || same || state?.busy} style={btn.secondaryXs}>Set</button>
            <button type="button" onClick={() => setOpen((x) => !x)} style={btn.linkXs} aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} codes and zips for ${city.name}`}>{open ? 'Hide' : 'Show'}</button>
            <Status state={state} />
          </span>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={6} style={s.td}>
          <div style={s.members} aria-label={`Codes and zips for ${city.name}`}>
            {city.codes.map((x) => (
              <span key={x.code} style={s.member}><code>{x.code}</code> {x.rule === 'follows' ? `follows ${x.followsCode} → ` : ''}{x.podId ? podLabel(pods, x.podId) : 'no pod'}</span>
            ))}
            {city.zips.map((z) => <span key={z.zip} style={s.member}><code>{z.zip}</code> {z.podId ? podLabel(pods, z.podId) : 'no pod'}</span>)}
          </div>
          <p style={s.hint}>To change one code or zip on its own, use Company codes or Zips below.</p>
        </td></tr>
      )}
    </>
  );
}

function Cities({ data, refresh }) {
  const [filter, setFilter] = useState('');
  const [splitOnly, setSplitOnly] = useState(false);
  const f = filter.trim().toLowerCase();
  const rows = data.cities.filter((x) => (!splitOnly || x.status === 'split')
    && (!f || x.name.toLowerCase().includes(f) || x.pods.some((p) => p.toLowerCase().includes(f) || podLabel(data.pods, p).toLowerCase().includes(f))));
  const splitCount = data.cities.filter((x) => x.status === 'split').length;
  return (
    <section style={s.card} aria-label="Cities">
      <h3 style={s.h3}>Cities</h3>
      <p style={s.blurb}>
        Every city the codes and zips name, with its pod. Setting a city&rsquo;s pod sets it on all of that city&rsquo;s
        codes and zips at once; new calls follow within one pull, and calls already taken stay where they are until you
        re-apply. {splitCount} {splitCount === 1 ? 'city is' : 'cities are'} split across pods zip by zip. Calls in the last {data.recentDays} days are shown.
      </p>
      <div style={s.inline}>
        <input aria-label="Filter cities" placeholder="Filter by city or pod" value={filter} onChange={(e) => setFilter(e.target.value)} style={s.text} />
        <label style={s.check}><input type="checkbox" checked={splitOnly} onChange={(e) => setSplitOnly(e.target.checked)} /> Split cities only</label>
      </div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr><th style={s.thS}>City</th><th style={s.thS}>Pod</th><th style={s.thNumS}>Codes</th><th style={s.thNumS}>Zips</th>
            <th style={s.thNumS}>Calls</th><th style={s.thS} /></tr></thead>
          <tbody>
            {rows.map((x) => <CityRow key={`${x.key}:${x.pods.join(',')}:${x.unassigned}`} city={x} pods={data.pods} refresh={refresh} />)}
            {!rows.length && <tr><td colSpan={6} style={s.td}>No city matches.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Not in the tables ───────────────────────────────────────────────────────

function Unmapped({ data, refresh }) {
  const [adding, setAdding] = useState(null);
  return (
    <section style={s.card} aria-label="Not in the tables">
      <h3 style={s.h3}>Seen on calls, not in the tables</h3>
      <p style={s.blurb}>
        Codes and zips callers gave in the last {data.unmappedDays} days that nothing maps. Their calls stay with the queue
        they rang. Adding one here applies to new calls; re-apply to move the ones already taken.
      </p>
      <div style={s.two}>
        <table style={s.table} aria-label="Unmapped codes">
          <thead><tr><th style={s.th}>Code</th><th style={s.thNum}>Calls</th><th style={s.th}>Last seen</th><th style={s.th} /></tr></thead>
          <tbody>
            {data.unmappedCodes.map((x) => (
              <tr key={x.code}><td style={s.td}><code>{x.code}</code></td><td style={s.num}>{fmtN(x.calls)}</td><td style={s.td}>{x.lastSeen}</td>
                <td style={s.td}><button type="button" onClick={() => setAdding({ kind: 'code', value: x.code })} style={btn.secondaryXs} aria-label={`Add code ${x.code}`}>Add</button></td></tr>
            ))}
            {!data.unmappedCodes.length && <tr><td colSpan={4} style={s.td}>Every code seen is mapped.</td></tr>}
          </tbody>
        </table>
        <table style={s.table} aria-label="Unmapped zips">
          <thead><tr><th style={s.th}>Zip</th><th style={s.thNum}>Calls</th><th style={s.th}>Last seen</th><th style={s.th} /></tr></thead>
          <tbody>
            {data.unmappedZips.map((x) => (
              <tr key={x.zip}><td style={s.td}><code>{x.zip}</code></td><td style={s.num}>{fmtN(x.calls)}</td><td style={s.td}>{x.lastSeen}</td>
                <td style={s.td}><button type="button" onClick={() => setAdding({ kind: 'zip', value: x.zip })} style={btn.secondaryXs} aria-label={`Add zip ${x.zip}`}>Add</button></td></tr>
            ))}
            {!data.unmappedZips.length && <tr><td colSpan={4} style={s.td}>Every zip seen is mapped.</td></tr>}
          </tbody>
        </table>
      </div>
      {adding?.kind === 'code' && <CodeEditor key={`c:${adding.value}`} isNew initial={{ code: adding.value, cityName: '', rule: 'pod', podId: '' }}
        pods={data.pods} codes={data.codes} refresh={refresh} onDone={() => setAdding(null)} />}
      {adding?.kind === 'zip' && <ZipEditor key={`z:${adding.value}`} isNew initial={{ zip: adding.value, cityName: '', podId: '' }}
        pods={data.pods} refresh={refresh} onDone={() => setAdding(null)} />}
    </section>
  );
}

// ── Re-apply ────────────────────────────────────────────────────────────────

function Reapply({ refresh }) {
  const [since, setSince] = useState('');
  const [preview, setPreview] = useState(null);
  const [state, run, setState] = useSave(refresh);
  const ask = async () => {
    setPreview(null);
    const r = await run(() => api.post('/contact-center/settings/pod-mapping/reapply', { since }), null);
    if (r) { setPreview(r); setState(null); }
  };
  const apply = async () => {
    if (!window.confirm(`Re-stamp ${fmtN(preview.changed)} call(s) from ${since} with the mapping as it stands now?`)) return;
    const r = await run(() => api.post('/contact-center/settings/pod-mapping/reapply', { since, commit: true }),
      (x) => `Done: ${fmtN(x.changed)} call(s) re-stamped, ${fmtN(x.podChanged)} of them to another pod.`);
    if (r) setPreview(null);
  };
  return (
    <section style={s.card} aria-label="Re-apply">
      <h3 style={s.h3}>Re-apply to past calls</h3>
      <p style={s.blurb}>
        Calls keep the pod they were given when they came in. To move calls since a date onto the mapping as it stands now,
        preview first; nothing changes until you apply. IRG calls never move. Calls that need the caller&rsquo;s zip and have
        none stored (calls from before October 2026) are left as they are, and counted.
      </p>
      <div style={s.inline}>
        <label style={s.check}>Since <input type="date" aria-label="Re-apply since" value={since}
          onChange={(e) => { setSince(e.target.value); setPreview(null); setState(null); }} style={s.textSm} /></label>
        <button type="button" onClick={ask} disabled={!since || state?.busy} style={btn.secondaryXs}>Preview</button>
        <Status state={state} />
      </div>
      {preview && (
        <div style={s.preview} aria-label="Re-apply preview">
          <p style={s.label}>
            From {preview.since}: {fmtN(preview.checked)} calls checked · {fmtN(preview.changed)} would change
            ({fmtN(preview.podChanged)} to another pod) · {fmtN(preview.noZip)} cannot be re-checked without a zip
          </p>
          {preview.moves.length > 0 && (
            <table style={s.table}>
              <thead><tr><th style={s.th}>From</th><th style={s.th}>To</th><th style={s.thNum}>Calls</th></tr></thead>
              <tbody>{preview.moves.map((m) => <tr key={`${m.from}>${m.to}`}><td style={s.td}>{m.from}</td><td style={s.td}>{m.to}</td><td style={s.num}>{fmtN(m.calls)}</td></tr>)}</tbody>
            </table>
          )}
          <div><button type="button" onClick={apply} disabled={!preview.changed || state?.busy} style={btn.primarySm}>Apply to {fmtN(preview.changed)} call(s)</button></div>
        </div>
      )}
    </section>
  );
}

// ── Change log ──────────────────────────────────────────────────────────────

function describe(l) {
  const a = l.after || {};
  const b = l.before || {};
  if (l.kind === 'reapply') return `Re-applied from ${l.key}: ${fmtN(a.changed)} call(s) re-stamped, ${fmtN(a.podChanged)} to another pod`;
  if (l.kind === 'pod' && a.mergedInto) return `Merged ${l.key} into ${a.mergedInto}: ${a.codes.length} code(s), ${a.zips.length} zip(s)`;
  if (l.kind === 'pod') {
    const parts = [];
    if (a.displayName !== b.displayName) parts.push(`renamed "${b.displayName}" to "${a.displayName}"`);
    if (a.retired !== b.retired) parts.push(a.retired ? 'retired' : 'restored');
    return `Pod ${l.key}: ${parts.join(', ')}`;
  }
  const rule = (x) => (x.retired ? 'retired' : x.rule === 'follows' ? `follows ${x.followsCode}` : x.rule === 'caller_zip' ? "caller's zip" : x.podId);
  if (l.kind === 'code') return l.before ? `Code ${l.key}: ${rule(b)} → ${rule(a)}${a.cityName !== b.cityName ? `, city "${b.cityName || ''}" → "${a.cityName || ''}"` : ''}` : `Code ${l.key} added: ${rule(a)}${a.cityName ? `, ${a.cityName}` : ''}`;
  if (l.kind === 'city') {
    return `City ${l.key}: ${(b.pods || []).join(', ') || 'no pod'} → ${a.podId} (${a.codes.length} code(s), ${a.zips.length} zip(s))${a.following?.length ? `; left following: ${a.following.join(', ')}` : ''}`;
  }
  if (l.kind === 'zip') return l.before ? `Zip ${l.key}: ${b.cityName} / ${b.podId} → ${a.cityName} / ${a.podId}` : `Zip ${l.key} added: ${a.cityName} / ${a.podId}`;
  return l.kind;
}

function ChangeLog({ log }) {
  const [all, setAll] = useState(false);
  const rows = all ? log : log.slice(0, 15);
  return (
    <section style={s.card} aria-label="Change log">
      <h3 style={s.h3}>Change log</h3>
      <table style={s.table}>
        <thead><tr><th style={s.th}>When</th><th style={s.th}>Who</th><th style={s.th}>Change</th></tr></thead>
        <tbody>
          {rows.map((l) => <tr key={l.id}><td style={s.td}>{fmtAsOf(l.changedAt)}</td><td style={s.td}>{l.by || '—'}</td><td style={s.td}>{describe(l)}</td></tr>)}
          {!log.length && <tr><td colSpan={3} style={s.td}>No changes yet.</td></tr>}
        </tbody>
      </table>
      {log.length > 15 && <div><button type="button" onClick={() => setAll((x) => !x)} style={btn.linkXs}>{all ? 'Show fewer' : `Show all ${log.length}`}</button></div>}
    </section>
  );
}

// ── The section ─────────────────────────────────────────────────────────────

export default function PodMapping() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cc-pod-mapping'], queryFn: () => api.get('/contact-center/settings/pod-mapping') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['cc-pod-mapping'] });
  const [addCode, setAddCode] = useState(null);
  const [addZip, setAddZip] = useState(null);
  const d = q.data;
  const cov = useMemo(() => {
    if (!d) return null;
    const { byAccount, byZip, unknown } = d.coverage;
    const total = byAccount + byZip + unknown;
    const pct = (n) => (total ? `${Math.round((n / total) * 1000) / 10}%` : '—');
    return { total, items: [['By account', byAccount, pct(byAccount)], ['By zip only', byZip, pct(byZip)], ['Not placed', unknown, pct(unknown)]] };
  }, [d]);

  return (
    <div style={s.wrap} aria-label="Pod mapping">
      <div style={s.headRow}>
        <h2 style={s.h2}>Pod mapping</h2>
        {cov && <span style={s.meta}>Last {d.recentDays} days: {cov.items.map(([k, n, p]) => `${k} ${fmtN(n)} (${p})`).join(' · ')}</span>}
      </div>
      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load the pod mapping: {q.error.message}</div>}
      {d && (
        <>
          <Unmapped data={d} refresh={refresh} />
          <Cities data={d} refresh={refresh} />
          <Codes data={d} refresh={refresh} addCode={addCode} setAddCode={setAddCode} />
          <Zips data={d} refresh={refresh} addZip={addZip} setAddZip={setAddZip} />
          <Pods pods={d.pods} refresh={refresh} />
          <Reapply refresh={refresh} />
          <ChangeLog log={d.log} />
        </>
      )}
    </div>
  );
}

const s = {
  wrap:   { display: 'flex', flexDirection: 'column', gap: '14px' },
  headRow:{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginTop: '8px' },
  card:   { ...card.base, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  h2:     { fontSize: '16px', fontWeight: 650, color: c.text, margin: 0 },
  h3:     { fontSize: '14px', fontWeight: 650, color: c.text, margin: 0 },
  blurb:  { fontSize: '12px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
  hint:   { fontSize: '11.5px', color: c.warningText, margin: 0, flexBasis: '100%' },
  meta:   { fontSize: '12px', color: c.textMuted },
  label:  { fontSize: '12px', fontWeight: 600, color: c.textStrong, margin: 0 },
  field:  { display: 'flex', flexDirection: 'column', gap: '4px' },
  check:  { fontSize: '12px', color: c.textStrong, display: 'flex', alignItems: 'center', gap: '6px' },
  inline: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  editor: { display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap', background: c.surfaceSunk, borderRadius: '8px', padding: '10px 12px' },
  text:   { fontSize: '12.5px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, minWidth: '180px' },
  textSm: { fontSize: '12.5px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, width: '110px' },
  select: { fontSize: '12.5px', padding: '5px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong },
  scroll: { maxHeight: '520px', overflow: 'auto' },
  two:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', alignItems: 'start' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:     { ...tbl.thSm, textAlign: 'left' },
  thNum:  { ...tbl.thRSm },
  // Headers that stay put while a long table scrolls inside its card.
  thS:    { ...tbl.thSm, textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 },
  thNumS: { ...tbl.thRSm, position: 'sticky', top: 0, zIndex: 1 },
  td:     { ...tbl.tdSm, color: c.textStrong },
  num:    { ...tbl.tdRSm, color: c.textStrong },
  faint:  { color: c.textFaint },
  warn:   { color: c.warningText, fontWeight: 600 },
  members:{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', fontSize: '12px', color: c.textStrong },
  member: { whiteSpace: 'nowrap' },
  ok:     { fontSize: '12px', color: c.successText },
  error:  { fontSize: '12px', color: c.dangerText },
  preview:{ background: c.surfaceSunk, borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' },
};
